import React, { useEffect, useRef, useState } from 'react';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { relabel } from '../../../../stays/wording';
import styled from '@emotion/styled';
import { StaysApiError } from '../../../../api/stays-client';
import {
  createMessageTemplate,
  MessageLanguage,
  MessageTemplate,
  updateMessageTemplate,
} from '../../../../api/stays-messages-client';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, FieldHint, Input, Label, Select } from '../../../form-kit';
import { FormModal } from '../../../modal/form-modal';
import { ChipGroup, ToggleChip } from '../../../toggle-chip/toggle-chip';
import toast from '../../../../utils/toast';
import {
  checkMessageTemplate,
  insertPlaceholder,
  MESSAGE_MAX,
  MESSAGE_PLACEHOLDERS,
  SENSITIVE_PLACEHOLDERS,
} from '../../../../utils/stays-template-placeholders';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { palette, spacing, typography } from '../../../../style-utils/theme';
import { LANGUAGE_OPTIONS } from './message-composer';

/**
 * NEW / EDIT TEMPLATE (plan S8, SC-3; R-S8.20, R-S8.21). Title (80), category
 * (40), language, content (2000) with "Insert a detail" chips — the sensitive
 * details marked "(only when released)": the composer fills them only once
 * S7's gate releases them. A discard asks first (isDirty).
 */

export const DETAIL_WORDS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  guestName: 'Guest name',
  partySize: 'Number of guests',
  listingName: 'Listing name',
  unitLabel: 'Unit',
  buildingAddress: 'Address',
  checkInDate: 'Check-in date',
  checkInTime: 'Check-in time',
  checkOutDate: 'Checkout date',
  checkOutTime: 'Checkout time',
  checkInLink: 'Check-in link',
  hostFirstName: 'Host first name',
  amountDue: 'Amount due',
  currency: 'Currency',
  source: 'Booking source',
  wifiName: 'Wi-Fi name',
  wifiPassword: 'Wi-Fi password',
  buildingCode: 'Building code',
  lockboxCode: 'Lockbox code',
  doorCode: 'Door code',
  guidePin: 'Guide PIN',
};

const Stack = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const GroupLabel = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.inkSoft};
`;

type Draft = { title: string; category: string; language: MessageLanguage; content: string };

export const MessageTemplateEditor: React.FC<{
  open: boolean;
  template: MessageTemplate | null;
  language: MessageLanguage;
  onClose: () => void;
  onSaved: (t: MessageTemplate) => void;
}> = ({ open, template, language, onClose, onSaved }) => {
  const blank: Draft = { title: '', category: '', language, content: '' };
  const [draft, setDraft] = useState<Draft>(blank);
  const [start, setStart] = useState<Draft>(blank);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const desk = useDeskInPlan();
  const area = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const d: Draft = template
      ? { title: template.title, category: template.category, language: template.language, content: template.content }
      : { title: '', category: '', language, content: '' };
    setDraft(d);
    setStart(d);
    setFields({});
  }, [open, template, language]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(start);
  const local = checkMessageTemplate(draft.content);
  const contentError =
    fields.content ||
    (local && local.code === 'TEMPLATE_PLACEHOLDER_UNKNOWN'
      ? `${local.placeholders.map(p => `{${p}}`).join(', ')} is not a detail a message can use.`
      : null);

  const insert = (name: string) => {
    const el = area.current;
    const out = insertPlaceholder(draft.content, name, el ? el.selectionStart : null, el ? el.selectionEnd : null);
    setDraft(d => ({ ...d, content: out.text }));
    window.requestAnimationFrame(() => {
      if (area.current) {
        area.current.focus();
        area.current.setSelectionRange(out.caret, out.caret);
      }
    });
  };

  const save = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (saving) return;
    const errors: Record<string, string> = {};
    if (!draft.title.trim()) errors.title = 'Enter a title.';
    if (!draft.content.trim()) errors.content = 'Write the message before saving.';
    if (Object.keys(errors).length) {
      setFields(errors);

      return;
    }
    setSaving(true);
    setFields({});
    try {
      const out = template
        ? await updateMessageTemplate(template.id, {
            title: draft.title,
            category: draft.category,
            content: draft.content,
            version: template.version,
          })
        : await createMessageTemplate(draft);
      toast.success(template ? 'Template saved' : 'Template added');
      onSaved(out.template);
    } catch (err) {
      const x = err as StaysApiError;
      if (x && x.field) setFields({ [x.field]: x.message });
      else if (x && x.status === 400 && Object.keys(x.fields || {}).length) setFields(x.fields);
      else toast.error(refusalText(x, 'Could not save the template.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open={open}
      title={template ? 'Edit template' : 'New template'}
      onClose={onClose}
      isDirty={dirty}
      footer={
        <AppButton type="button" variant="primary" loading={saving} loadingLabel="Saving" onClick={() => void save()}>
          Save template
        </AppButton>
      }
    >
      <Stack onSubmit={save} noValidate aria-label={template ? 'Edit template' : 'New template'}>
        <Field>
          <Label htmlFor="msg-title">Title</Label>
          <Input id="msg-title" value={draft.title} maxLength={80} hasError={!!fields.title}
            onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} />
          <FieldError message={fields.title} />
        </Field>
        <Field>
          <Label htmlFor="msg-category">Category</Label>
          <Input id="msg-category" value={draft.category} maxLength={40} hasError={!!fields.category}
            aria-describedby="msg-category-hint" onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} />
          <FieldHint id="msg-category-hint">For example Arrival or Departure</FieldHint>
          <FieldError message={fields.category} />
        </Field>
        <Field>
          <Label htmlFor="msg-language">Language</Label>
          <Select id="msg-language" value={draft.language} disabled={!!template}
            onChange={e => setDraft(d => ({ ...d, language: e.target.value as MessageLanguage }))}>
            {LANGUAGE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <CountedTextarea ref={area} id="msg-content" label="Content" value={draft.content} max={MESSAGE_MAX} rows={8}
            hasError={!!contentError} onChange={e => setDraft(d => ({ ...d, content: e.target.value }))} />
          <FieldError message={contentError || undefined} />
        </Field>
        <GroupLabel>Insert a detail</GroupLabel>
        <ChipGroup aria-label="Insert a detail">
          {[...MESSAGE_PLACEHOLDERS, ...SENSITIVE_PLACEHOLDERS].map(p => {
            const words = `${relabel(DETAIL_WORDS[p] || p, desk)}${SENSITIVE_PLACEHOLDERS.includes(p) ? ' (only when released)' : ''}`;

            return (
              <ToggleChip key={p} pressed={false} onToggle={() => insert(p)} aria-label={`Insert ${words}`}>
                {words}
              </ToggleChip>
            );
          })}
        </ChipGroup>
      </Stack>
    </FormModal>
  );
};
